import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { fmtMoney, fmtNumber } from "../components/ops/opsUtils";
import { ArrowUpRight, AlertTriangle, Search, GitMerge, Check, X } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function AttorneysIndex() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [mergeGroup, setMergeGroup] = useState(null); // emails[]

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/operations/attorneys");
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    const rows = data.attorneys.slice().sort((a, b) => (b.funded_volume || 0) - (a.funded_volume || 0));
    if (!q) return rows;
    return rows.filter((a) => (a.email || "").toLowerCase().includes(q) || (a.name || "").toLowerCase().includes(q) || (a.firm || "").toLowerCase().includes(q));
  }, [data, search]);

  if (loading) return <div className="p-10 text-[#6B7280]" data-testid="attorneys-loading">Loading attorneys…</div>;

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="attorneys-index">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="uppercase-label">Operations</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]">Attorneys</h1>
          <div className="text-sm text-[#6B7280] mt-1.5">{fmtNumber(data.attorneys.length)} firms · production rankings</div>
        </div>
      </div>

      {(data.probable_duplicate_groups || []).length > 0 && (
        <div className="bg-[#FFFBEB] border border-[#FCD34D] rounded-lg p-4 mb-4" data-testid="dupe-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#B45309] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-[#92400E]">Probable duplicate attorneys detected</div>
              <div className="text-[#92400E] mt-1 text-xs">Same firm, different email casing or punctuation. Merge each group to a canonical email so production & active-balance stats are accurate.</div>
              <div className="mt-3 space-y-2">
                {data.probable_duplicate_groups.map((g, i) => (
                  <div key={i} className="flex items-center justify-between bg-white border border-[#FCD34D] rounded-md px-3 py-2" data-testid={`dupe-group-${i}`}>
                    <div className="text-sm text-[#0A2540] truncate">{g.join("   ·   ")}</div>
                    <Button size="sm" variant="outline" onClick={() => setMergeGroup(g)} data-testid={`merge-group-${i}-btn`}><GitMerge className="w-3.5 h-3.5 mr-1.5" /> Merge</Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
          <Input placeholder="Search by name, firm, or email…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" data-testid="attorneys-search" />
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
              <th className="px-4 py-2.5 font-medium">Attorney</th>
              <th className="px-4 py-2.5 font-medium text-right">Funded volume</th>
              <th className="px-4 py-2.5 font-medium text-right">Active balance</th>
              <th className="px-4 py-2.5 font-medium text-center">Active</th>
              <th className="px-4 py-2.5 font-medium text-center">Settled</th>
              <th className="px-4 py-2.5 font-medium text-center">Total</th>
              <th className="px-4 py-2.5 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-[#6B7280]" data-testid="attorneys-empty">No attorneys match your search.</td></tr>
            )}
            {filtered.map((a, i) => {
              const rowKey = a.email || `unassigned::${(a.name || "").toLowerCase()}::${(a.firm || "").toLowerCase()}::${i}`;
              const isUnassigned = !!a.unassigned || !a.email;
              const displayName = a.name || (a.email ? a.email.split("@")[0] : "Unassigned attorney");
              const handleRowClick = () => {
                if (isUnassigned) return;   // Don't route to a broken /admin/attorneys/ URL
                window.location.href = `/admin/attorneys/${encodeURIComponent(a.email)}`;
              };
              return (
              <tr key={rowKey} data-testid={`attorney-row-${i}`}
                  onClick={handleRowClick}
                  className={`border-b border-[#F3F4F6] transition-colors ${isUnassigned ? "bg-[#FFFBEB]/40 cursor-default" : "hover:bg-[#F9FAFB] cursor-pointer"}`}>
                <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                  {isUnassigned ? (
                    <>
                      <div className="text-[#0A2540] font-medium flex items-center gap-2" data-testid={`attorney-row-${i}-name`}>
                        {displayName}
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#FEF3C7] text-[#92400E] border border-[#FCD34D] rounded-full px-2 py-0.5">
                          <AlertTriangle className="w-3 h-3" /> No email on file
                        </span>
                      </div>
                      <div className="text-[11px] text-[#9CA3AF]">
                        {a.firm ? `${a.firm} · ` : ""}Assign an attorney email to these {a.total_count || 0} loan{(a.total_count || 0) === 1 ? "" : "s"} to reconcile
                      </div>
                    </>
                  ) : (
                    <>
                      <Link to={`/admin/attorneys/${encodeURIComponent(a.email)}`} className="text-[#0A2540] font-medium hover:text-[#0066FF]" data-testid={`attorney-row-${i}-name`}>{displayName}</Link>
                      <div className="text-[11px] text-[#9CA3AF]">{a.email}{a.firm ? ` · ${a.firm}` : ""}</div>
                    </>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right text-[#0A2540] tabular-nums font-medium">{fmtMoney(a.funded_volume || 0)}</td>
                <td className="px-4 py-2.5 text-right text-[#0066FF] tabular-nums">{fmtMoney(a.active_balance || 0)}</td>
                <td className="px-4 py-2.5 text-center text-[#0A2540] tabular-nums">{a.active_count || 0}</td>
                <td className="px-4 py-2.5 text-center text-[#6B7280] tabular-nums">{a.settled_count || 0}</td>
                <td className="px-4 py-2.5 text-center text-[#0A2540] tabular-nums font-medium">{a.total_count || 0}</td>
                <td className="px-4 py-2.5 text-right text-[#0066FF]">{isUnassigned ? null : <ArrowUpRight className="w-3.5 h-3.5 inline" />}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {mergeGroup && (
        <MergeModal emails={mergeGroup} onClose={() => setMergeGroup(null)} onMerged={() => { setMergeGroup(null); load(); }} />
      )}
    </div>
  );
}

function MergeModal({ emails, onClose, onMerged }) {
  const [canonical, setCanonical] = useState(emails[0]);
  const [canonicalName, setCanonicalName] = useState("");
  const [firm, setFirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const merge = async () => {
    setSubmitting(true);
    try {
      const aliases = emails.filter((e) => e !== canonical);
      await api.post("/operations/attorneys/merge", {
        canonical_email: canonical,
        aliases,
        canonical_name: canonicalName || undefined,
        firm: firm || undefined,
      });
      toast.success(`Merged ${aliases.length} alias${aliases.length !== 1 ? "es" : ""} into ${canonical}`);
      onMerged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-[#0A2540]/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="merge-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <div className="font-medium text-[#0A2540] flex items-center gap-2"><GitMerge className="w-4 h-4 text-[#0066FF]" /> Merge attorneys</div>
          <button onClick={onClose} className="text-[#6B7280] hover:text-[#0A2540]" data-testid="merge-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <div className="text-sm text-[#374151] mb-2">Select the <strong>canonical</strong> email — all loans pointing to the others will be re-pointed to this one and the aliases will be removed.</div>
            <div className="space-y-2">
              {emails.map((e) => (
                <label key={e} className={`flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer transition-colors ${canonical === e ? "border-[#0066FF] bg-[#F0F6FF]" : "border-[#E5E7EB] hover:border-[#0066FF]/40"}`} data-testid={`canonical-option-${e}`}>
                  <input type="radio" name="canonical" checked={canonical === e} onChange={() => setCanonical(e)} className="accent-[#0066FF]" />
                  <span className="text-sm text-[#0A2540] flex-1">{e}</span>
                  {canonical === e && <Check className="w-4 h-4 text-[#0066FF]" />}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#6B7280]">Canonical name (optional)</label>
              <Input value={canonicalName} onChange={(e) => setCanonicalName(e.target.value)} placeholder="e.g. Sergey Kats, Esq." className="mt-1 h-9" data-testid="canonical-name" />
            </div>
            <div>
              <label className="text-xs text-[#6B7280]">Firm name (optional)</label>
              <Input value={firm} onChange={(e) => setFirm(e.target.value)} placeholder="e.g. Kats Personal Injury Law" className="mt-1 h-9" data-testid="canonical-firm" />
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#E5E7EB] bg-[#F9FAFB] flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={merge} disabled={submitting} className="bg-[#0066FF] hover:bg-[#0052CC]" data-testid="confirm-merge">
            {submitting ? "Merging…" : `Merge ${emails.length - 1} into 1`}
          </Button>
        </div>
      </div>
    </div>
  );
}
